import { useState, useCallback } from 'react';

export type OrderType = 'delivery' | 'pickup';

export interface FormData {
  name: string;
  phone: string;
  email: string;
  specialRequests: string;
  address_line_1?: string;
  address_line_2?: string;
  locality?: string;
  administrative_district_level_1?: string;
  postal_code?: string;
  country?: string;
}

export interface FormErrors {
  [key: string]: string | undefined;
}

export const useCheckoutForm = (initialOrderType: OrderType = 'pickup') => {
  const [activeTab, setActiveTab] = useState<OrderType>(initialOrderType);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    phone: '',
    email: '',
    specialRequests: '',
    address_line_1: '',
    address_line_2: '',
    locality: '',
    administrative_district_level_1: '',
    postal_code: '',
    country: 'US',
  });
  const [errors, setErrors] = useState<FormErrors>({});

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    
    // Clear error when field is edited
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined,
      }));
    }
  }, [errors]);

  const validateForm = useCallback((): boolean => {
    const newErrors: FormErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\d{10,15}$/.test(formData.phone.replace(/[^\d]/g, ''))) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    
    // Make email optional - only validate format if provided
    if (formData.email.trim() && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    // Validate delivery address fields if delivery is selected
    if (activeTab === 'delivery') {
      if (!formData.address_line_1?.trim()) {
        newErrors.address_line_1 = 'Address is required for delivery';
      }
      if (!formData.locality?.trim()) {
        newErrors.locality = 'City is required for delivery';
      }
      if (!formData.administrative_district_level_1?.trim()) {
        newErrors.administrative_district_level_1 = 'State is required for delivery';
      }
      if (!formData.postal_code?.trim()) {
        newErrors.postal_code = 'ZIP code is required for delivery';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, activeTab]);

  const resetForm = useCallback(() => {
    setFormData({
      name: '',
      phone: '',
      email: '',
      specialRequests: '',
      address_line_1: '',
      address_line_2: '',
      locality: '',
      administrative_district_level_1: '',
      postal_code: '',
      country: 'US',
    });
    setErrors({});
  }, []);

  return {
    activeTab,
    setActiveTab,
    formData,
    errors,
    handleChange,
    validateForm,
    resetForm,
  };
};
